import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  IconButton,
  Typography,
  MenuItem,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Psychology as IntelligenceIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { useUpdate, useNotify, useRefresh, useDataProvider } from 'react-admin';

interface LeadIntelligenceDialogProps {
  open: boolean;
  onClose: () => void;
  record: any;
}

export const LeadIntelligenceDialog: React.FC<LeadIntelligenceDialogProps> = ({ open, onClose, record }) => {
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  
  const getInitialFormData = () => ({
    status: record?.status || '',
    source: record?.source || '',
    company: record?.company || '',
    estimatedValue: record?.estimatedValue || 0,
    customerTypeId: record?.customerTypeId || null,
    assignedUserId: record?.assignedUserId ? parseInt(record.assignedUserId, 10) : null,
    displayName: record?.displayName || '',
  });

  const [formData, setFormData] = useState(getInitialFormData());
  const [customerTypes, setCustomerTypes] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customerTypesResponse, usersResponse] = await Promise.all([
          dataProvider.getList('crm/customer-types', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'name', order: 'ASC' },
            filter: {},
          }),
          dataProvider.getList('users', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'name', order: 'ASC' },
            filter: {},
          })
        ]);
        setCustomerTypes(customerTypesResponse.data);
        // Sort users alphabetically by name or email
        const sortedUsers = usersResponse.data.sort((a, b) => {
          const nameA = (a.name || a.email || '').toLowerCase();
          const nameB = (b.name || b.email || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setUsers(sortedUsers);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    if (open) {
      fetchData();
      // Reset form data to current record values when opening
      setFormData(getInitialFormData());
    }
  }, [open, dataProvider]);

  // Update form data when record changes
  useEffect(() => {
    if (record) {
      setFormData(getInitialFormData());
    }
  }, [record]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    let value: any = event.target.value;
    
    // Handle numeric conversions
    if (field === 'estimatedValue') {
      value = parseFloat(event.target.value) || 0;
    } else if (field === 'assignedUserId') {
      // Convert to number or null
      value = event.target.value === '' ? null : parseInt(event.target.value, 10);
    } else if (field === 'customerTypeId') {
      // Convert empty string to null for foreign key
      value = event.target.value === '' ? null : event.target.value;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await update('leads', {
        id: record.id,
        data: formData,
        previousData: record,
      });
      notify('Lead intelligence updated successfully', { type: 'success' });
      refresh(); // This will refresh the LeadShow page with updated data
      onClose();
    } catch (error) {
      notify('Error updating lead intelligence', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original record values
    setFormData(getInitialFormData());
    onClose();
  };

  const leadStatuses = [
    { value: 'New', label: 'New' },
    { value: 'Contacted', label: 'Contacted' },
    { value: 'Qualified', label: 'Qualified' },
    { value: 'Proposal', label: 'Proposal' },
    { value: 'Converted', label: 'Converted' },
    { value: 'Lost', label: 'Lost' },
  ];

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'new':
        return '#1976D2'; // Blue - fresh, needs attention
      case 'contacted':
        return '#F57C00'; // Orange - in progress, warming up
      case 'qualified':
        return '#388E3C'; // Green - positive, moving forward
      case 'proposal':
        return '#7B1FA2'; // Purple - advanced stage
      case 'converted':
        return '#00ACC1'; // Teal - success, completed
      case 'lost':
        return '#D32F2F'; // Red - negative outcome
      default:
        return '#666666'; // Grey - unknown/default
    }
  };

  const leadSources = [
    { value: 'Website', label: 'Website' },
    { value: 'Referral', label: 'Referral' },
    { value: 'Advertisement', label: 'Advertisement' },
    { value: 'SocialMedia', label: 'Social Media' },
    { value: 'Event', label: 'Event' },
    { value: 'ColdCall', label: 'Cold Call' },
    { value: 'Other', label: 'Other' },
  ];

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IntelligenceIcon color="primary" />
            <Typography variant="h6">Lead Intelligence</Typography>
          </Box>
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Basic Information
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Display Name"
              value={formData.displayName}
              onChange={handleChange('displayName')}
              variant="outlined"
              helperText="A unique display name for this lead"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Lead Status & Source
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Status"
              value={formData.status}
              onChange={handleChange('status')}
              variant="outlined"
            >
              {leadStatuses.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: getStatusColor(status.value),
                      }}
                    />
                    {status.label}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Lead Source"
              value={formData.source}
              onChange={handleChange('source')}
              variant="outlined"
            >
              {leadSources.map((source) => (
                <MenuItem key={source.value} value={source.value}>
                  {source.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Company Information
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Company"
              value={formData.company}
              onChange={handleChange('company')}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Customer Type"
              value={formData.customerTypeId}
              onChange={handleChange('customerTypeId')}
              variant="outlined"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {customerTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: type.color,
                      }}
                    />
                    {type.name}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>


          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Estimated Value"
              type="number"
              value={formData.estimatedValue}
              onChange={handleChange('estimatedValue')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MoneyIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              options={users}
              getOptionLabel={(option) => option.name || option.email || 'Unknown User'}
              value={users.find(user => user.id === formData.assignedUserId) || null}
              onChange={(event, newValue) => {
                setFormData(prev => ({
                  ...prev,
                  assignedUserId: newValue ? newValue.id : null,
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assigned User"
                  variant="outlined"
                  helperText="Who is responsible for this lead"
                  placeholder="Search users..."
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Typography variant="body1">
                    {option.name || option.email}
                  </Typography>
                </Box>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              clearOnBlur
              handleHomeEndKeys
            />
          </Grid>

        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};